// MainView.tsx
import React, { useCallback } from "react";
import { Route, useNavigate, Routes, Navigate } from "react-router-dom";
import {
  FormattedMessage,
  IntlProvider,
  MessageFormatElement,
} from "react-intl";
import localeData from "../../intl/index";
import { AppBar, Box, Typography, Drawer, Button, CircularProgress } from "@mui/material";
import { useConfig } from "../../hooks/ConfigContext";
import { MainMenu } from "../Shared/SideMenu/MainMenu";
import { Feedback } from "../components/Feedback";
import { ReportingView } from "./ReportingView";
import Header from "../Shared/Header/Header";
import SignIn from "../components/SignIn";
import PrivateRoute from "../Shared/PrivateRoute/PrivateRoute";
import { useUser } from "../../context/UserContext"; // Import initializeUser function
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MenuIcon from '@mui/icons-material/Menu';

export const MainView: React.FC = () => {
  const cfg = useConfig();
  const { restart, config } = cfg;
  const navigate = useNavigate();
  const { user, loading } = useUser(); // Get loading state from useUser hook
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };
  const loginPageUrl: string | undefined = cfg.config.loginPageUrl;
  //Split locales with a region code
  const languageWithoutRegionCode = config.language
    .toLowerCase()
    .split(/[_-]+/)[0];
  //Try full locale, try locale without region code, then fallback
  const messages:
    | Record<string, string>
    | Record<string, MessageFormatElement[]>
    | undefined =
    (localeData as any)[languageWithoutRegionCode] ||
    (localeData as any)[config.language] ||
    localeData.fi;

  const login = useCallback(() => {
    window.location.href = loginPageUrl || cfg.config.uiUrl!;
  }, [cfg.config.uiUrl, loginPageUrl]);

  const restartApp = useCallback(() => {
    restart();
    navigate("/");
  }, [navigate, restart]);

  const paddingLeft = user.email && isDrawerOpen ? "20px" : "50px";
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size="100px" />
      </Box>
    );
  }

  return (
    <IntlProvider locale={config.language || "fi"} messages={messages}>
      <Box display="flex" flexDirection="column" margin={0} padding={0}>
        <Box display="flex" padding={0} height={"5rem"}>
          <AppBar
            elevation={0}
            position="fixed"
            sx={{
              backgroundColor: "white",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            <Header />
          </AppBar>
        </Box>


        <Box sx={{ marginTop: '20px', position: 'relative' }}>
          <Button onClick={handleDrawerOpen} sx={{ marginLeft: '25px', position: 'absolute', top: '0px' }}>
            <MenuIcon fontSize="large" />
          </Button>

          <Drawer
            variant="persistent"
            anchor="left"
            open={isDrawerOpen}
            sx={{
              width: 270,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: 270,
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              <MainMenu />
            </Box>
            <Button onClick={handleDrawerClose}>
              <ArrowBackIosNewIcon fontSize="medium" />
            </Button>
          </Drawer>

          <div style={{ marginLeft: isDrawerOpen ? 270 : 0, transition: 'margin 0.3s' }}>
            <Box sx={{ paddingLeft, marginTop: '10px' }}>
              {config.loginRequired ? (
                <Box>
                  <Typography variant="h3" color="error">
                    <FormattedMessage id={"message.authenticationError"} defaultMessage={"Error"} />
                  </Typography>
                  <Box mt={3} mb={3}>
                    <FormattedMessage
                      id={"message.loginErrorMessage"}
                      defaultMessage={"Please login"}
                    />
                  </Box>
                  <Button
                    size="large"
                    variant="contained"
                    sx={{ backgroundColor: (theme) => theme.palette.success.light }}
                    onClick={login}
                  >
                    <FormattedMessage id={"button.login"} defaultMessage={"Login"} />
                  </Button>
                </Box>
              ) : config.technicalError ? (
                <Box>
                  <Typography variant="h3" color="error">
                    <FormattedMessage id={"message.error"} defaultMessage={"Error"} />
                  </Typography>
                  <Box mt={3} mb={3}>
                    <FormattedMessage
                      id={"message.errorMessage"}
                      defaultMessage={"Contact support if this problem occurs again"}
                    />
                  </Box>
                  <Button size="large" variant="contained" onClick={restartApp}>
                    <FormattedMessage id={"button.startAgain"} defaultMessage={"Start again"} />
                  </Button>
                </Box>
              ) : (
                <Box width="100%">
                  <Routes>
                    <Route path="/reports" element={<PrivateRoute><ReportingView /></PrivateRoute>} />
                    <Route path="/help" element={<PrivateRoute><ReportingView /></PrivateRoute>} />
                    <Route path="/signin" element={<SignIn />} />
                    <Route path="/*" element={<Navigate to="/signin" replace />} />
                  </Routes>
                </Box>
              )}
            </Box>
          </div>
        </Box>


      </Box>
      <Feedback />
    </IntlProvider>
  );
};
